import { FC } from 'react';
import { observer } from 'mobx-react-lite';

import type { ActiveTemplateField } from '../../../api/types';

import type { FieldMode } from './fieldInput.types';
import { FieldShell } from './FieldShell';
import { FileField } from './FileField';
import { ListChipsField } from './ListChipsField';
import { ReferenceField } from './ReferenceField';
import { ScalarField } from './ScalarField';
import { TableGridField } from './TableGridField';

interface Props {
  field: ActiveTemplateField;
  mode: FieldMode;
  /** Draft value when editing; the persisted current value when reading. */
  value: unknown;
  dirty?: boolean;
  disabled?: boolean;
  /** Validation message from react-hook-form, shown beneath the input. */
  error?: string;
  onChange?: (value: unknown) => void;
}

const LIST_TYPES = new Set(['multi_select', 'string_list', 'number_list', 'list']);

/** Dispatches a field to its per-type renderer, wrapped in the shared shell. */
export const FieldRenderer: FC<Props> = observer(
  ({ field, mode, value, dirty, disabled, error, onChange }) => {
    const inputProps = { field, value, mode, dirty, disabled, onChange };

    let input;
    if (field.type === 'table') {
      input = <TableGridField {...inputProps} />;
    } else if (field.type === 'file') {
      input = <FileField {...inputProps} />;
    } else if (field.type === 'reference') {
      input = <ReferenceField {...inputProps} />;
    } else if (LIST_TYPES.has(field.type)) {
      input = <ListChipsField {...inputProps} />;
    } else {
      input = <ScalarField {...inputProps} />;
    }

    return (
      <FieldShell field={field} mode={mode} dirty={dirty} error={error}>
        {input}
      </FieldShell>
    );
  },
);
